import React, { useCallback, useEffect, useState } from "react";
import { api, extractError } from "../../lib/api";
import { SectionCard, Pill } from "../Ui";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { bagScopeLabel } from "../../lib/bagSizes";
import { labourActivityLabel } from "../../lib/labourActivities";

function formatInr(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function DalaTruckList({ title, trucks, tone = "muted", emptyText }) {
  if (!trucks?.length) {
    return (
      <div className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
        {emptyText}
      </div>
    );
  }
  return (
    <div className="rounded-md border border-border overflow-hidden">
      <div className={`px-3 py-2 text-xs font-medium border-b border-border ${tone === "gold" ? "bg-amber-50 text-amber-950" : "bg-muted/30"}`}>
        {title}
      </div>
      <ul className="divide-y divide-border text-xs">
        {trucks.map((t) => (
          <li key={`${t.purchase_id}-${t.purchase_code}`} className="flex items-center justify-between gap-2 px-3 py-2">
            <span className="font-mono">{t.purchase_code}</span>
            <span className="text-muted-foreground tabular">{t.quantity} qtl</span>
            <span className="font-medium tabular">{formatInr(t.amount)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function LabourDayBatchReview({
  canReview,
  canPost = false,
  approveLabel = "Approve for posting",
  showAggregate = false,
  title = "Daily labour settlement",
  description = "Review contractor batches for the selected work date. Dala deducted on freight vouchers is shown separately from cash dala payable to the contractor.",
}) {
  const [workDate, setWorkDate] = useState(new Date().toISOString().slice(0, 10));
  const [showAllOpen, setShowAllOpen] = useState(true);
  const [batches, setBatches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!canReview) return;
    try {
      const { data } = await api.get("/labour/day-batches", {
        params: showAllOpen ? {} : { work_date: workDate },
      });
      const rows = (data || []).filter((b) => (showAllOpen ? b.state !== "POSTED" : true));
      const visible = showAllOpen ? rows : rows.filter((b) => b.work_date === workDate);
      setBatches(visible);
      if (selected?.id) {
        const fresh = visible.find((b) => b.id === selected.id);
        setSelected(fresh || null);
      }
    } catch (err) {
      toast.error(extractError(err));
    }
  }, [workDate, showAllOpen, canReview, selected?.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const aggregate = async () => {
    try {
      setBusy(true);
      await api.post("/labour/day-batches/aggregate", null, { params: { work_date: workDate } });
      toast.success("Day batches refreshed");
      reload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const approve = async (batchId) => {
    try {
      setBusy(true);
      const { data } = await api.post(`/labour/day-batches/${batchId}/approve`, {});
      toast.success("Batch finalized");
      setSelected(data);
      reload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const postBatch = async (batchId) => {
    try {
      setBusy(true);
      const { data } = await api.post(`/labour/day-batches/${batchId}/post`);
      toast.success("Posted to general ledger");
      setSelected(data);
      reload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!canReview) {
    return <p className="text-sm text-muted-foreground py-6 text-center">No permission to review labour batches.</p>;
  }

  const dalaSummary = selected?.dala_summary;
  const pendingCount = batches.filter((b) => b.state === "PENDING_REVIEW").length;

  return (
    <div className="space-y-4" data-testid="labour-day-batch-review">
      {title && (
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Work date filter</Label>
          <Input
            type="date"
            value={workDate}
            onChange={(e) => setWorkDate(e.target.value)}
            className="mt-1 w-40"
            disabled={showAllOpen}
          />
        </div>
        <label className="flex items-center gap-2 text-xs pb-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showAllOpen}
            onChange={(e) => setShowAllOpen(e.target.checked)}
            className="rounded border-border"
          />
          Show all open batches
        </label>
        {showAggregate && (
          <Button type="button" variant="outline" size="sm" onClick={aggregate} disabled={busy}>
            Rebuild from unloading
          </Button>
        )}
        <Button type="button" variant="outline" size="sm" onClick={reload} disabled={busy}>
          Reload
        </Button>
        {pendingCount > 0 && (
          <Pill tone="warning">{pendingCount} pending review</Pill>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Contractor batches">
          <div className="space-y-2 max-h-[24rem] overflow-y-auto">
            {batches.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => setSelected(b)}
                className={`w-full text-left rounded-md border p-3 text-sm ${
                  selected?.id === b.id ? "border-bansal-sage bg-muted/60" : "border-border hover:bg-muted/30"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{b.party_name}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-muted-foreground tabular">{b.work_date}</span>
                    <Pill tone={b.state === "POSTED" ? "sage" : b.state === "APPROVED" ? "warning" : "muted"}>
                      {b.state === "PENDING_REVIEW" ? "Pending" : b.state}
                    </Pill>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Labour {formatInr(b.labour_subtotal)} · Dala {formatInr(b.dala_subtotal)} · Total {formatInr(b.total_amount)}
                </p>
              </button>
            ))}
            {!batches.length && (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No open batches{showAllOpen ? "" : ` for ${workDate}`}. Click &quot;Rebuild from unloading&quot; after supervisors assign contractors.
              </p>
            )}
          </div>
        </SectionCard>

        <SectionCard title={selected ? selected.party_name : "Batch detail"}>
          {selected ? (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="rounded border p-2">
                  <p className="text-muted-foreground">Labour</p>
                  <p className="font-semibold tabular">{formatInr(selected.labour_subtotal)}</p>
                </div>
                <div className="rounded border p-2">
                  <p className="text-muted-foreground">Dala</p>
                  <p className="font-semibold tabular">{formatInr(selected.dala_subtotal)}</p>
                </div>
                <div className="rounded border p-2">
                  <p className="text-muted-foreground">Total</p>
                  <p className="font-semibold tabular">{formatInr(selected.total_amount)}</p>
                </div>
              </div>

              {selected.dala_subtotal > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Dala settlement</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <DalaTruckList
                      title={`Deducted on freight voucher (${formatInr(dalaSummary?.freight_deducted_total)})`}
                      trucks={dalaSummary?.freight_deducted}
                      emptyText="No trucks with dala deducted on freight."
                    />
                    <DalaTruckList
                      title={`Cash dala payable to contractor (${formatInr(dalaSummary?.cash_collected_total)})`}
                      trucks={dalaSummary?.cash_collected}
                      tone="gold"
                      emptyText="No cash dala payable for this batch."
                    />
                  </div>
                </div>
              )}

              <div className="rounded-md border overflow-x-auto max-h-48 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Truck</th>
                      <th className="text-left p-2">Activity</th>
                      <th className="text-right p-2">Qty</th>
                      <th className="text-right p-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selected.lines || []).map((ln) => (
                      <tr key={ln.id} className="border-t border-border">
                        <td className="p-2 font-mono">{ln.purchase_code}</td>
                        <td className="p-2">
                          {labourActivityLabel(ln.activity_type)}
                          {ln.bag_scope ? ` · ${bagScopeLabel(ln.bag_scope)}` : ""}
                          {ln.dala_source === "CASH" && (
                            <span className="ml-1 text-amber-700">· cash</span>
                          )}
                          {ln.dala_source === "FREIGHT_DEDUCT" && (
                            <span className="ml-1 text-muted-foreground">· freight</span>
                          )}
                        </td>
                        <td className="p-2 text-right tabular">{ln.quantity}</td>
                        <td className="p-2 text-right tabular">{formatInr(ln.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap gap-2">
                {selected.state === "PENDING_REVIEW" && (
                  <Button type="button" size="sm" onClick={() => approve(selected.id)} disabled={busy}>
                    {approveLabel}
                  </Button>
                )}
                {canPost && selected.state === "APPROVED" && (
                  <Button
                    type="button"
                    size="sm"
                    className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
                    onClick={() => postBatch(selected.id)}
                    disabled={busy}
                  >
                    Mark posted
                  </Button>
                )}
                {selected.state === "POSTED" && (
                  <Pill tone="sage">Posted (sync in Tally)</Pill>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-8 text-center">Select a contractor batch to review lines.</p>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
